import { ReactElement, useRef } from "react";
import { useLocation, useMatch } from "react-router-dom";
import useConceptDetails from "catalog/hooks/useConceptDetails";
import useConceptsLookup from "catalog/hooks/useConceptsLookup";
import useConceptsTree from "catalog/hooks/useConceptsTree";
import { getTreeNodeById, mapIdToLink } from "catalog/utils/concept";

interface BreadcrumbInfo {
  label?: string;
  href?: string;
  loading?: boolean;
  badge?: ReactElement<SVGElement>;
}

const useConceptBreadcrumbs = () => {
  const location = useLocation();
  const conceptsPath = "/data-browser/dashboards/concepts";
  const stickyCrumb = useRef<BreadcrumbInfo[]>([]);
  const isDetailsView = location.pathname.endsWith("/details");
  const match = useMatch(`${conceptsPath}/:id/*`);
  const activeId = match?.params.id;
  const { data: lookup, isLoading: loadingLookup } = useConceptsLookup();
  const { data: tree, isLoading: loadingTree } = useConceptsTree();
  const { data: conceptDetails, isLoading: loadingDetails } = useConceptDetails(
    isDetailsView ? activeId : undefined
  );

  const breadcrumbs: BreadcrumbInfo[] = [];

  if (!activeId) {
    // At root level, just return categories title
    return breadcrumbs;
  }

  if (lookup && tree) {
    if (isDetailsView) {
      // If details view, get parent IDs from concept detail response and active concept name
      const conceptDetail = Object.values(conceptDetails?.data ?? {}).find((val) => val);

      if (conceptDetail) {
        const mappedParentIds = conceptDetail.parent_ids
          .map((id) => mapIdToLink(id, lookup))
          .filter(Boolean) as BreadcrumbInfo[];

        breadcrumbs.push(...mappedParentIds, {
          label: conceptDetail.concept_name,
          href: `${conceptsPath}/${conceptDetail.concept_id}/details`,
        });
      }
    } else {
      if (activeId) {
        const { node, parentIds } = getTreeNodeById(activeId, tree.data);
        const mappedParentIds = parentIds
          .map((id) => mapIdToLink(id, lookup))
          .filter(Boolean) as BreadcrumbInfo[];
        breadcrumbs.push(...mappedParentIds);

        if (node) {
          const mappedActiveBreadcrumb = mapIdToLink(activeId, lookup);
          if (mappedActiveBreadcrumb) {
            breadcrumbs.push(mappedActiveBreadcrumb);
          }
        }
      }
    }
  }

  if (!loadingTree && !loadingLookup && !loadingDetails) {
    // Handle using the last breadcrumb if jumping from concept group list -> concept details view
    // where parent_ids are sourced from different async responses
    stickyCrumb.current = breadcrumbs;
  }

  return stickyCrumb.current;
};

export default useConceptBreadcrumbs;
